/**
 * Workflow Serialization Utilities
 * Helper functions for workflow manipulation
 */

import { WorkflowDefinition, WorkflowNode, WorkflowEdge } from './types';

export class WorkflowUtils {
  /**
   * Clone a workflow definition (deep copy)
   */
  static clone(workflow: WorkflowDefinition): WorkflowDefinition {
    return JSON.parse(JSON.stringify(workflow));
  }

  /**
   * Merge two workflows (useful for templates)
   */
  static merge(
    base: WorkflowDefinition,
    override: Partial<WorkflowDefinition>
  ): WorkflowDefinition {
    return {
      ...base,
      ...override,
      nodes: override.nodes || base.nodes,
      edges: override.edges || base.edges,
      configuration: {
        ...base.configuration,
        ...override.configuration
      },
      metadata: {
        ...base.metadata,
        ...override.metadata
      }
    };
  }

  /**
   * Get workflow statistics
   */
  static getStats(workflow: WorkflowDefinition): {
    nodeCount: number;
    edgeCount: number;
    agentNodes: number;
    approvalNodes: number;
    decisionNodes: number;
  } {
    const nodeTypes = workflow.nodes.reduce((acc, node) => {
      acc[node.type] = (acc[node.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      nodeCount: workflow.nodes.length,
      edgeCount: workflow.edges.length,
      agentNodes: nodeTypes['agent'] || 0,
      approvalNodes: nodeTypes['approval'] || 0,
      decisionNodes: nodeTypes['decision'] || 0
    };
  }

  /**
   * Find node by ID
   */
  static findNode(workflow: WorkflowDefinition, nodeId: string): WorkflowNode | undefined {
    return workflow.nodes.find(node => node.id === nodeId);
  }

  /**
   * Find edges connected to a node
   */
  static findConnectedEdges(
    workflow: WorkflowDefinition,
    nodeId: string
  ): { incoming: WorkflowEdge[]; outgoing: WorkflowEdge[] } {
    const incoming = workflow.edges.filter(edge => edge.target === nodeId);
    const outgoing = workflow.edges.filter(edge => edge.source === nodeId);
    return { incoming, outgoing };
  }

  /**
   * Get execution order (topological sort)
   */
  static getExecutionOrder(workflow: WorkflowDefinition): string[] {
    const adjacencyList = new Map<string, string[]>();
    const inDegree = new Map<string, number>();

    // Initialize
    workflow.nodes.forEach(node => {
      adjacencyList.set(node.id, []);
      inDegree.set(node.id, 0);
    });

    // Build graph
    workflow.edges.forEach(edge => {
      adjacencyList.get(edge.source)?.push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    });

    // Topological sort using Kahn's algorithm
    const queue: string[] = [];
    const result: string[] = [];

    // Find nodes with no incoming edges
    inDegree.forEach((degree, nodeId) => {
      if (degree === 0) queue.push(nodeId);
    });

    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      result.push(nodeId);

      const neighbors = adjacencyList.get(nodeId) || [];
      neighbors.forEach(neighbor => {
        const newDegree = (inDegree.get(neighbor) || 0) - 1;
        inDegree.set(neighbor, newDegree);
        if (newDegree === 0) queue.push(neighbor);
      });
    }

    return result;
  }

  /**
   * Export workflow to downloadable JSON file
   */
  static exportToFile(workflow: WorkflowDefinition, filename?: string): void {
    const json = JSON.stringify({ version: '1.0.0', workflow }, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `${workflow.name.replace(/\s+/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Import workflow from file
   */
  static async importFromFile(file: File): Promise<WorkflowDefinition> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = e.target?.result as string;
          const parsed = JSON.parse(json);
          const workflow = parsed.workflow || parsed;
          resolve(workflow);
        } catch (error: any) {
          reject(new Error(`Failed to parse file: ${error.message}`));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }
}

